import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Calendar, Megaphone, CreditCard, Building2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

const API = "/api";

const BusinessPortal = () => {
  const { user, token, login } = useAuth();
  const [business, setBusiness] = useState(null);
  const [myAds, setMyAds] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showAdDialog, setShowAdDialog] = useState(false);
  const [showBusinessDialog, setShowBusinessDialog] = useState(false);

  // Form states
  const [eventForm, setEventForm] = useState({
    title: '', venue: '', description: '', date: '', time: '', image_url: '', ticket_url: ''
  });
  const [adForm, setAdForm] = useState({
    title: '', business_name: '', description: '', image_url: '', link_url: '', is_premium: false
  });
  const [businessForm, setBusinessForm] = useState({ name: '', description: '' });

  useEffect(() => {
    if (user && token) {
      fetchBusinessData();
    } else {
      setLoading(false);
    }
  }, [user, token]);

  const fetchBusinessData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [businessRes, adsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/business/me`, { headers }),
        axios.get(`${API}/business/my-ads`, { headers }),
        axios.get(`${API}/business/my-events`, { headers })
      ]);
      setBusiness(businessRes.data);
      setMyAds(adsRes.data);
      setMyEvents(eventsRes.data);
    } catch (error) {
      console.error('Failed to fetch business data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createBusiness = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/business`, businessForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusiness(response.data);
      setShowBusinessDialog(false);
      toast.success('Business account created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create business');
    }
  };

  const createEvent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/events`, eventForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowEventDialog(false);
      setEventForm({ title: '', venue: '', description: '', date: '', time: '', image_url: '', ticket_url: '' });
      fetchBusinessData();
      toast.success('Event created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    }
  };

  const createAd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/ads`, { ...adForm, business_name: business?.name || adForm.business_name }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowAdDialog(false);
      setAdForm({ title: '', business_name: '', description: '', image_url: '', link_url: '', is_premium: false });
      fetchBusinessData();
      toast.success('Ad created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ad');
    }
  };

  const deleteEvent = async (id) => {
    try {
      await axios.delete(`${API}/events/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBusinessData();
      toast.success('Event deleted');
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const deleteAd = async (id) => {
    try {
      await axios.delete(`${API}/ads/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBusinessData();
      toast.success('Ad deleted');
    } catch (error) {
      toast.error('Failed to delete ad');
    }
  };

  const handleSubscribe = async () => {
    try {
      const response = await axios.post(`${API}/business/subscribe`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.info(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Subscription failed');
    }
  };

  // Not logged in
  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="business-login-prompt">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-6"
        >
          <Building2 size={64} className="mx-auto text-primary" />
          <h1 className="font-heading text-3xl font-bold">Business Portal</h1>
          <p className="text-muted-foreground max-w-md">
            Sign in to create and manage your business account, post events, and advertise to the Twin Cities community.
          </p>
          <Button onClick={login} size="lg" className="gap-2" data-testid="business-login-btn">
            Sign In to Get Started
          </Button>
        </motion.div>
      </div>
    );
  }

  // No business account yet
  if (!business) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="create-business-prompt">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-6 max-w-lg"
        >
          <Building2 size={64} className="mx-auto text-primary" />
          <h1 className="font-heading text-3xl font-bold">Create Your Business</h1>
          <p className="text-muted-foreground">
            Set up your business account to start posting events and ads to reach the Twin Cities community.
          </p>
          
          <Dialog open={showBusinessDialog} onOpenChange={setShowBusinessDialog}>
            <DialogTrigger asChild>
              <Button size="lg" className="gap-2" data-testid="create-business-trigger">
                <Plus size={18} />
                Create Business Account
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Business Account</DialogTitle>
              </DialogHeader>
              <form onSubmit={createBusiness} className="space-y-4">
                <div>
                  <Label htmlFor="business-name">Business Name</Label>
                  <Input
                    id="business-name"
                    value={businessForm.name}
                    onChange={(e) => setBusinessForm(p => ({ ...p, name: e.target.value }))}
                    required
                    data-testid="business-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="business-desc">Description</Label>
                  <Textarea
                    id="business-desc"
                    value={businessForm.description}
                    onChange={(e) => setBusinessForm(p => ({ ...p, description: e.target.value }))}
                    data-testid="business-desc-input"
                  />
                </div>
                <Button type="submit" className="w-full" data-testid="submit-business-btn">
                  Create Business
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </motion.div>
      </div>
    );
  }

  // Business dashboard
  return (
    <div className="space-y-8" data-testid="business-dashboard">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
      >
        <div>
          <p className="text-xs text-muted-foreground tracking-widest uppercase">Business Portal</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="business-name-display">{business.name}</h1>
          <p className="text-muted-foreground mt-1">{business.description}</p>
        </div>
        
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-sm text-xs font-medium ${
            business.subscription_status === 'active' 
              ? 'bg-secondary/20 text-secondary' 
              : 'bg-muted text-muted-foreground'
          }`}>
            {business.subscription_status === 'active' ? 'Premium' : 'Free Tier'}
          </span>
          {business.subscription_status !== 'active' && (
            <Button onClick={handleSubscribe} variant="outline" size="sm" className="gap-2" data-testid="subscribe-btn">
              <CreditCard size={16} />
              Upgrade to Premium
            </Button>
          )}
        </div>
      </motion.div>

      {/* Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-2 md:grid-cols-4 gap-4"
      >
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardDescription>Active Events</CardDescription>
            <CardTitle className="text-2xl font-heading" data-testid="events-count">{myEvents.length}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardDescription>Active Ads</CardDescription>
            <CardTitle className="text-2xl font-heading" data-testid="ads-count">{myAds.length}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardDescription>Premium Ads</CardDescription>
            <CardTitle className="text-2xl font-heading">{myAds.filter(a => a.is_premium).length}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardDescription>Account Status</CardDescription>
            <CardTitle className="text-lg font-heading capitalize">{business.subscription_status}</CardTitle>
          </CardHeader>
        </Card>
      </motion.div>

      {/* Tabs */}
      <Tabs defaultValue="events" className="space-y-6">
        <TabsList className="bg-muted">
          <TabsTrigger value="events" className="gap-2" data-testid="events-tab">
            <Calendar size={16} />
            Events
          </TabsTrigger>
          <TabsTrigger value="ads" className="gap-2" data-testid="ads-tab">
            <Megaphone size={16} />
            Ads
          </TabsTrigger>
        </TabsList>

        {/* Events Tab */}
        <TabsContent value="events" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="font-heading text-xl font-bold">Your Events</h2>
            <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="create-event-trigger">
                  <Plus size={16} />
                  Create Event
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Create Event</DialogTitle>
                </DialogHeader>
                <form onSubmit={createEvent} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Event Title</Label>
                      <Input
                        value={eventForm.title}
                        onChange={(e) => setEventForm(p => ({ ...p, title: e.target.value }))}
                        required
                        data-testid="event-title-input"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Venue</Label>
                      <Input
                        value={eventForm.venue}
                        onChange={(e) => setEventForm(p => ({ ...p, venue: e.target.value }))}
                        required
                        data-testid="event-venue-input"
                      />
                    </div>
                    <div>
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={eventForm.date}
                        onChange={(e) => setEventForm(p => ({ ...p, date: e.target.value }))}
                        required
                        data-testid="event-date-input"
                      />
                    </div>
                    <div>
                      <Label>Time</Label>
                      <Input
                        value={eventForm.time}
                        onChange={(e) => setEventForm(p => ({ ...p, time: e.target.value }))}
                        placeholder="7:00 PM"
                        required
                        data-testid="event-time-input"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Description</Label>
                      <Textarea
                        value={eventForm.description}
                        onChange={(e) => setEventForm(p => ({ ...p, description: e.target.value }))}
                        data-testid="event-desc-input"
                      />
                    </div>
                    <div>
                      <Label>Image URL</Label>
                      <Input
                        value={eventForm.image_url}
                        onChange={(e) => setEventForm(p => ({ ...p, image_url: e.target.value }))}
                        placeholder="https://..."
                      />
                    </div>
                    <div>
                      <Label>Ticket URL</Label>
                      <Input
                        value={eventForm.ticket_url}
                        onChange={(e) => setEventForm(p => ({ ...p, ticket_url: e.target.value }))}
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full" data-testid="submit-event-btn">
                    Create Event
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {myEvents.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="py-8 text-center text-muted-foreground">
                No events yet. Create your first event to reach the Twin Cities community.
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {myEvents.map((event) => (
                <Card key={event.id} className="bg-card border-border">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {event.image_url && (
                          <img src={event.image_url} alt="" className="w-16 h-16 object-cover rounded-sm" />
                        )}
                        <div>
                          <h3 className="font-medium">{event.title}</h3>
                          <p className="text-sm text-muted-foreground">{event.venue} • {event.date}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteEvent(event.id)}
                        data-testid={`delete-event-${event.id}`}
                      >
                        <Trash2 size={16} className="text-destructive" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Ads Tab */}
        <TabsContent value="ads" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="font-heading text-xl font-bold">Your Ads</h2>
            <Dialog open={showAdDialog} onOpenChange={setShowAdDialog}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="create-ad-trigger">
                  <Plus size={16} />
                  Create Ad
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Create Ad</DialogTitle>
                </DialogHeader>
                <form onSubmit={createAd} className="space-y-4">
                  <div>
                    <Label>Ad Title</Label>
                    <Input
                      value={adForm.title}
                      onChange={(e) => setAdForm(p => ({ ...p, title: e.target.value }))}
                      required
                      data-testid="ad-title-input"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={adForm.description}
                      onChange={(e) => setAdForm(p => ({ ...p, description: e.target.value }))}
                      data-testid="ad-desc-input"
                    />
                  </div>
                  <div>
                    <Label>Image URL</Label>
                    <Input
                      value={adForm.image_url}
                      onChange={(e) => setAdForm(p => ({ ...p, image_url: e.target.value }))}
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <Label>Link URL</Label>
                    <Input
                      value={adForm.link_url}
                      onChange={(e) => setAdForm(p => ({ ...p, link_url: e.target.value }))}
                      placeholder="https://..."
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-sm bg-muted">
                    <div>
                      <p className="font-medium">Premium Ad</p>
                      <p className="text-xs text-muted-foreground">Featured placement (requires subscription)</p>
                    </div>
                    <Switch
                      checked={adForm.is_premium}
                      onCheckedChange={(checked) => setAdForm(p => ({ ...p, is_premium: checked }))}
                      data-testid="ad-premium-switch"
                    />
                  </div>
                  <Button type="submit" className="w-full" data-testid="submit-ad-btn">
                    Create Ad
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {myAds.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="py-8 text-center text-muted-foreground">
                No ads yet. Create your first ad to promote your business.
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {myAds.map((ad) => (
                <Card key={ad.id} className="bg-card border-border">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {ad.image_url && (
                          <img src={ad.image_url} alt="" className="w-16 h-16 object-cover rounded-sm" />
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium">{ad.title}</h3>
                            {ad.is_premium && (
                              <span className="px-2 py-0.5 text-xs bg-primary/20 text-primary rounded-sm">Premium</span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{ad.description}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteAd(ad.id)}
                        data-testid={`delete-ad-${ad.id}`}
                      >
                        <Trash2 size={16} className="text-destructive" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BusinessPortal;
